import { create } from 'zustand';

export type DebugCategory =
  | 'wire'
  | 'render'
  | 'input'
  | 'chat'
  | 'world'
  | 'entities'
  | 'inventory'
  | 'building'
  | 'ui'
  | 'audio'
  | 'network';

export type DebugLogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface DebugEntry {
  id: number;
  timestamp: number;
  category: DebugCategory;
  level: DebugLogLevel;
  message: string;
  entityId?: string;
}

const ALL_CATEGORIES: DebugCategory[] = [
  'wire', 'render', 'input', 'chat', 'world',
  'entities', 'inventory', 'building', 'ui', 'audio', 'network',
];

function defaultCategories(): Record<DebugCategory, boolean> {
  const m = {} as Record<DebugCategory, boolean>;
  for (const c of ALL_CATEGORIES) m[c] = true;
  return m;
}

// Ring buffer internals — module scope, not zustand state
const CAPACITY = 1000;
const ring: (DebugEntry | null)[] = new Array<DebugEntry | null>(CAPACITY).fill(null);
let writeIdx = 0;
let nextId = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function ringWrite(entry: Omit<DebugEntry, 'id' | 'timestamp'>): void {
  const full: DebugEntry = { ...entry, id: nextId++, timestamp: Date.now() };
  ring[writeIdx] = full;
  writeIdx = (writeIdx + 1) % CAPACITY;
}

function ringSnapshot(): DebugEntry[] {
  const out: DebugEntry[] = [];
  for (let i = 0; i < CAPACITY; i++) {
    const idx = (writeIdx + i) % CAPACITY;
    const e = ring[idx];
    if (e) out.push(e);
  }
  return out;
}

export interface DebugConsoleState {
  open: boolean;
  categories: Record<DebugCategory, boolean>;
  severity: DebugLogLevel;
  entries: DebugEntry[];

  toggle: () => void;
  setCategory: (cat: DebugCategory, enabled: boolean) => void;
  setSeverity: (level: DebugLogLevel) => void;
  push: (entry: Omit<DebugEntry, 'id' | 'timestamp'>) => void;
}

const LEVEL_RANK: Record<DebugLogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

export { ALL_CATEGORIES, LEVEL_RANK };

export const useDebugStore = create<DebugConsoleState>((set) => ({
  open: false,
  categories: defaultCategories(),
  severity: 'debug',
  entries: [],

  toggle: () => set((s) => ({ open: !s.open, entries: !s.open ? ringSnapshot() : s.entries })),

  setCategory: (cat, enabled) =>
    set((s) => ({
      categories: { ...s.categories, [cat]: enabled },
    })),

  setSeverity: (level) => set({ severity: level }),

  push: (entry) => {
    ringWrite(entry);
    if (!flushTimer) {
      flushTimer = setTimeout(() => {
        flushTimer = null;
        set({ entries: ringSnapshot() });
      }, 250);
    }
  },
}));
